"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CheckerInput,
  MarkedSentence,
  ToneLegend,
} from "@/components/tools/style-checkers-a/a-frame";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type CommaSpliceReport,
  findCommaSplices,
  type SpliceFinding,
  type SpliceFixKind,
  type SpliceKind,
  type SpliceNote,
} from "@/lib/tool-kit/style-checkers/comma-splice";

/** English strings. A translated content file overrides these through `ui`. */
export const COMMA_SPLICE_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, an essay section or a whole draft…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "The survey closed on Friday, it had reached 400 responses. Most participants were students, however, a few were staff. The results were clear, they were consistent across both groups, and the pattern held after we removed outliers. Although the sample was small, the effect was large. The interviews were transcribed, then two raters reviewed them. The team repeated the study, but the effect disappeared. I came, I saw. There was no control group, there was no way to rule out a placebo effect.",
  showPossible: "Show possible splices too",
  enterPrompt:
    "Paste or type some text to see its likely comma splices — two complete clauses joined by only a comma.",
  results: "Results",
  likely: "Likely splices",
  possible: "Possible splices",
  sentences: "Sentences checked",
  withSplice: "Sentences with a splice",
  commas: "Commas examined",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with comma splices highlighted",
  legendLikely: "Likely splice",
  legendPossible: "Possible splice",
  hoverHint:
    "Each highlight runs from the word before the comma to the word after it. Hover over one for the reason; the cards below give the fixes.",
  noneFound:
    "No comma splices found. That does not prove there are none: the checker looks for clauses that open with a pronoun, a determiner, a name or a conjunctive adverb, and it can miss others.",
  onlyPossible:
    "Only possible splices were found, and they are hidden. Tick “Show possible splices too” to see them.",
  findingsHeading: "Each flagged comma, with fixes",
  confidenceLikely: "Likely",
  confidencePossible: "Possible",
  leftClause: "Before the comma",
  rightClause: "After the comma",
  fixesHeading: "Ways to fix it",
  fixPeriod: "Two sentences",
  fixSemicolon: "Semicolon",
  fixConjunction: "Comma and a conjunction",
  copy: "Copy",
  copied: "Copied!",
  reasonPronoun:
    "A subject pronoun and its verb follow the comma, so the words after it can stand alone as a sentence.",
  reasonThere:
    "“There is/are…” follows the comma and opens a complete clause of its own.",
  reasonDemonstrative:
    "“This”, “these” or “those” and a verb follow the comma: a complete clause of its own.",
  reasonDeterminer:
    "A noun phrase with its own verb follows the comma: a complete clause of its own.",
  reasonName:
    "A name with its own verb follows the comma: a complete clause of its own.",
  reasonAdverb:
    "“{adverb}” is a conjunctive adverb, not a conjunction, so it cannot join two clauses after a comma alone.",
  noteShort:
    "Both clauses are very short. Short parallel clauses are sometimes joined by a comma on purpose.",
  noteParallel:
    "The two clauses open the same way. Parallel clauses are sometimes deliberate, especially in speeches and fiction.",
  noteSeries:
    "Several clauses in a row are joined by commas. A deliberate series is sometimes accepted, but check each one.",
  judgement:
    "A heuristic, not a parser: it flags commas where both sides look like complete clauses. You decide whether each is an error, a deliberate style choice or a misreading.",
} as const;

type Strings = typeof COMMA_SPLICE_CHECKER_STRINGS;

const TONES: Record<string, HighlightTone> = {
  likely: "rose",
  possible: "amber",
};

const REASONS: Record<Exclude<SpliceKind, "adverb">, keyof Strings> = {
  pronoun: "reasonPronoun",
  there: "reasonThere",
  demonstrative: "reasonDemonstrative",
  determiner: "reasonDeterminer",
  name: "reasonName",
};

const NOTES: Record<SpliceNote, keyof Strings> = {
  short: "noteShort",
  parallel: "noteParallel",
  series: "noteSeries",
};

const FIX_LABELS: Record<SpliceFixKind, keyof Strings> = {
  period: "fixPeriod",
  semicolon: "fixSemicolon",
  conjunction: "fixConjunction",
};

function reasonFor(finding: SpliceFinding, strings: Strings): string {
  if (finding.kind === "adverb") {
    return t(strings.reasonAdverb, { adverb: finding.adverb ?? "" });
  }
  return strings[REASONS[finding.kind]];
}

function explain(finding: SpliceFinding, strings: Strings): string {
  const reason = reasonFor(finding, strings);
  return finding.note ? `${reason} ${strings[NOTES[finding.note]]}` : reason;
}

function FindingCard({
  finding,
  strings,
}: {
  finding: SpliceFinding;
  strings: Strings;
}) {
  const tone = TONES[finding.confidence];
  return (
    <li className={`${CARD} space-y-3`}>
      <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {finding.confidence === "likely"
          ? strings.confidenceLikely
          : strings.confidencePossible}
      </p>
      <MarkedSentence
        from={finding.start - finding.sentenceStart}
        sentence={finding.sentence}
        to={finding.end - finding.sentenceStart}
        tone={tone}
      />
      <p className={HINT}>{explain(finding, strings)}</p>
      <dl className="grid gap-1 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-4">
        <dt className="font-medium text-foreground">{strings.leftClause}</dt>
        <dd className="text-muted-foreground">{finding.leftClause}</dd>
        <dt className="font-medium text-foreground">{strings.rightClause}</dt>
        <dd className="text-muted-foreground">{finding.rightClause}</dd>
      </dl>
      <div className="space-y-2">
        <p className="font-medium text-foreground text-sm">
          {strings.fixesHeading}
        </p>
        <ul className="space-y-2">
          {finding.fixes.map((fix) => (
            <li
              className="flex flex-wrap items-start justify-between gap-2 text-sm"
              key={fix.kind}
            >
              <span className="min-w-0 flex-1">
                <span className="font-medium text-foreground">
                  {strings[FIX_LABELS[fix.kind]]}:
                </span>{" "}
                <span className="text-muted-foreground">{fix.sentence}</span>
              </span>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                small
                text={fix.sentence}
              />
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
}

function Stats({
  report,
  strings,
}: {
  report: CommaSpliceReport;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <StatGrid>
      <StatCard emphasis label={strings.likely} value={fmt(report.likely)} />
      <StatCard label={strings.possible} value={fmt(report.possible)} />
      <StatCard
        label={strings.withSplice}
        value={fmt(report.sentencesWithSplice)}
      />
      <StatCard label={strings.sentences} value={fmt(report.sentences)} />
      <StatCard label={strings.commas} value={fmt(report.commas)} />
    </StatGrid>
  );
}

export default function CommaSpliceCheckerWidget() {
  const strings = useToolUi(COMMA_SPLICE_CHECKER_STRINGS);
  const [text, setText] = useState("");
  const [showPossible, setShowPossible] = useState(true);
  const deferred = useDeferredValue(text);
  const report = useMemo(() => findCommaSplices(deferred), [deferred]);
  const shown = report.findings.filter(
    (finding) => showPossible || finding.confidence === "likely"
  );
  const highlights: TextMatch[] = shown.map((finding) => ({
    start: finding.start,
    end: finding.end,
    category: finding.confidence,
    title: explain(finding, strings),
  }));
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <CheckerInput
        clearLabel={strings.clear}
        example={strings.exampleText}
        exampleLabel={strings.tryExample}
        extra={
          <Checkbox
            checked={showPossible}
            label={strings.showPossible}
            onChange={setShowPossible}
          />
        }
        hint={strings.privacy}
        id="csc-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        value={text}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <Stats report={report} strings={strings} />
            <p className={HINT}>{strings.judgement}</p>
          </div>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <ToneLegend
              items={[
                { tone: "rose", label: strings.legendLikely },
                { tone: "amber", label: strings.legendPossible },
              ]}
            />
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {report.findings.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : null}
          {report.findings.length > 0 && shown.length === 0 ? (
            <Notice>{strings.onlyPossible}</Notice>
          ) : null}

          {shown.length > 0 ? (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.findingsHeading}</h3>
              <ul className="space-y-3">
                {shown.map((finding) => (
                  <FindingCard
                    finding={finding}
                    key={finding.comma}
                    strings={strings}
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
